/**
 * Админ-панель уведомлений (`/team/notifications`, canon admin-notifications, api-contract §3h).
 * Доступ — только администратор Центрики (роут под RequireRole admin). Заменяет заглушку Блока 8.
 *
 * Три секции (§3h):
 *  - «Виды писем» — список встроенных видов: тумблер вкл/выкл (критичные виды требуют подтверждения
 *    при выключении → PATCH `{enabled:false, confirm:true}`); удаления нет. У `training_reminder`
 *    (editableOffsets) — редактор офсетов напоминаний (дни): добавить/убрать/изменить, без гейта.
 *    У письма команде о новом заказе (editableRecipients, team-new-order-email) — редактор
 *    получателей (адреса администратора и менеджеров), тоже без гейта: текст письма он не меняет.
 *  - «Редактор шаблона» — тема + текст + чипы доступных меток (клик вставляет `{{метка}}`),
 *    кнопки «Предпросмотр» и «Тест себе»; «Сохранить» ЗАБЛОКИРОВАН, пока не выполнены И предпросмотр,
 *    И тест-отправка (фронт-гейт безопасности §3h); правка темы/текста сбрасывает гейт. 422 (чужая
 *    метка) — показывается инлайн.
 *  - «Ручная рассылка» — тема + текст + выбор клиентов и/или контактов → разовая рассылка (sent N).
 *
 * Над секциями — панель «Отправка писем» (email-daily-limit): счётчик писем и состояние
 * дневного лимита (`EmailStatsPanel`).
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Bell, Mail, Plus, Send, Trash2 } from 'lucide-react'
import {
  ApiError,
  adminApi,
  teamApi,
  teamClientApi,
  type AdminTemplate,
  type ClientSummary,
  type Contact,
  type TemplatePreview,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { EmailStatsPanel } from './email-stats-panel'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field, RequiredMark } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Switch } from '@/design-system/components/primitives/switch'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Hint } from '@/design-system/components/primitives/text'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

/** Стили текстового поля (textarea) — те же токены, что у Input (зеркало order-detail). */
const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

/**
 * Письмо оформляет кабинет (email-layout): к тексту добавляются ссылка на кабинет, подпись и
 * строка «отвечать не нужно», приветствие «Здравствуйте!» выносится в отдельный абзац. Подсказка
 * под полями текста — чтобы подпись не вписывали в шаблон второй раз.
 */
const EMAIL_LAYOUT_HINT =
  'Ссылку на личный кабинет, подпись «С уважением, Центрика» и строку о том, что отвечать на письмо не нужно, кабинет добавит сам — вписывать их в текст не требуется.'

/** Извлекает читаемый список недопустимых меток из ответа 422 (детали полей либо текст). */
function invalidPlaceholderMessages(err: unknown): string[] {
  if (err instanceof ApiError && err.details && err.details.length > 0) {
    return err.details.map((d) => d.message)
  }
  return []
}

// --- Секция «Виды писем»: тумблеры вкл/выкл + защита критичных видов ---

export function TemplatesList({
  templates,
  selectedKind,
  onSelect,
  onUpdated,
}: {
  templates: AdminTemplate[]
  selectedKind: string | null
  onSelect: (kind: string) => void
  onUpdated: (template: AdminTemplate) => void
}) {
  const [busyKind, setBusyKind] = useState<string | null>(null)
  const [confirmKind, setConfirmKind] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function applyEnabled(template: AdminTemplate, enabled: boolean, confirm: boolean) {
    setBusyKind(template.kind)
    setError(null)
    try {
      const updated = await adminApi.notifications.patchTemplate(template.kind, {
        enabled,
        ...(confirm ? { confirm: true } : {}),
      })
      onUpdated(updated)
      setConfirmKind(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось изменить вид письма'))
    } finally {
      setBusyKind(null)
    }
  }

  function onToggle(template: AdminTemplate, next: boolean) {
    setError(null)
    // Критичный вид при выключении — только через явное подтверждение (§3h, защита входа).
    if (!next && template.critical) {
      setConfirmKind(template.kind)
      return
    }
    void applyEnabled(template, next, false)
  }

  return (
    <Card header="Виды писем" className="mb-4">
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <ul className="divide-y divide-line">
        {templates.map((t) => (
          <li key={t.kind} className="py-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => onSelect(t.kind)}
                className="min-w-0 flex-1 text-left"
              >
                <span
                  className={
                    selectedKind === t.kind ? 'font-semibold text-accent-txt' : 'font-medium text-txt'
                  }
                >
                  {t.label}
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-2">
                  {t.critical ? <Badge variant="amber">Критичный</Badge> : null}
                  {t.editableOffsets ? <Badge variant="blue">Настраиваемое расписание</Badge> : null}
                  {t.editableRecipients ? <Badge variant="blue">Письмо команде</Badge> : null}
                  <Hint>{t.enabled ? 'Включён' : 'Выключен'}</Hint>
                </span>
              </button>
              <Switch
                aria-label={`${t.enabled ? 'Выключить' : 'Включить'} вид «${t.label}»`}
                checked={t.enabled}
                disabled={busyKind === t.kind}
                onCheckedChange={(next) => onToggle(t, next)}
              />
            </div>

            {confirmKind === t.kind ? (
              <Alert
                variant="danger"
                className="mt-3"
                title="Выключить критичный вид письма?"
                actions={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConfirmKind(null)}
                      disabled={busyKind === t.kind}
                    >
                      Отмена
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void applyEnabled(t, false, true)}
                      disabled={busyKind === t.kind}
                    >
                      {busyKind === t.kind ? 'Выключение…' : 'Выключить'}
                    </Button>
                  </div>
                }
              >
                Этот вид обеспечивает вход пользователя. Без него письма входа перестанут отправляться.
              </Alert>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  )
}

// --- Редактор офсетов напоминаний (training_reminder, editableOffsets) ---

/**
 * Пояснение к расписанию вида. У уведомления об окончании абонентского обслуживания по обучению
 * (training-subscription-expiry-notice) пороги считаются до окончания договора и «догоняются»:
 * если порог пропущен (договор внесли позже), уведомление приходит при ближайшей проверке.
 */
function offsetsHint(kind: string): string {
  if (kind === 'training_subscription_expiring') {
    return 'За сколько дней до окончания договора уведомлять. По умолчанию — за 30 и за 20 дней. Если договор внесён позже порога, уведомление придёт при ближайшей ежедневной проверке.'
  }
  return 'За сколько дней до срока отправлять письмо. По умолчанию — 60, 30 и 7 дней.'
}

/** Строка офсета со стабильной идентичностью (uid) — чтобы key не сдвигался при удалении из середины. */
type OffsetRow = { uid: number; value: string }

let offsetRowSeq = 0
function makeOffsetRow(value: string): OffsetRow {
  offsetRowSeq += 1
  return { uid: offsetRowSeq, value }
}

function OffsetsEditor({
  template,
  onUpdated,
}: {
  template: AdminTemplate
  onUpdated: (template: AdminTemplate) => void
}) {
  const [days, setDays] = useState<OffsetRow[]>(() =>
    template.offsets.map((d) => makeOffsetRow(String(d))),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  function change(uid: number, value: string) {
    setOk(false)
    setDays((prev) => prev.map((row) => (row.uid === uid ? { ...row, value } : row)))
  }

  function remove(uid: number) {
    setOk(false)
    setDays((prev) => prev.filter((row) => row.uid !== uid))
  }

  function add() {
    setOk(false)
    setDays((prev) => [...prev, makeOffsetRow('')])
  }

  async function save() {
    const parsed = days
      .map((row) => Number.parseInt(row.value, 10))
      .filter((n) => Number.isFinite(n) && n > 0)
    setSaving(true)
    setError(null)
    setOk(false)
    try {
      const updated = await adminApi.notifications.patchTemplate(template.kind, { offsets: parsed })
      onUpdated(updated)
      setDays(updated.offsets.map((d) => makeOffsetRow(String(d))))
      setOk(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить расписание'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-5 border-t border-line pt-4">
      <div className="mb-1 text-13 font-medium text-txt">Расписание напоминаний</div>
      <Hint className="mb-3">{offsetsHint(template.kind)}</Hint>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {ok ? (
        <Alert variant="info" className="mb-3">
          Расписание сохранено.
        </Alert>
      ) : null}
      <div className="flex flex-col gap-2">
        {days.map((row, i) => (
          <div key={row.uid} className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              aria-label={`Дней до срока, напоминание ${i + 1}`}
              value={row.value}
              onChange={(e) => change(row.uid, e.target.value)}
              className="w-28"
            />
            <span className="text-13 text-txt-secondary">дн. до срока</span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              aria-label={`Убрать напоминание ${i + 1}`}
              onClick={() => remove(row.uid)}
              leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
            >
              Убрать
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={add}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить день
        </Button>
        <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить расписание'}
        </Button>
      </div>
    </div>
  )
}

// --- Редактор получателей письма команде (order_submitted, editableRecipients) ---

/** Строка адреса со стабильной идентичностью (uid) — чтобы key не сдвигался при удалении из середины. */
type RecipientRow = { uid: number; value: string }

let recipientRowSeq = 0
/**
 * Пояснения редактора получателей по виду письма команде: о чём письмо и что происходит, пока
 * адресов нет. Виды: «Новая заявка» (team-new-order-email) и «Абонентское обслуживание по обучению
 * заканчивается» (training-subscription-expiry-notice).
 */
function recipientsHint(kind: string): { intro: string; empty: string } {
  if (kind === 'training_subscription_expiring') {
    return {
      intro:
        'Адреса администратора и менеджеров, на которые приходит письмо об окончании абонентского обслуживания.',
      empty:
        'Пока адресов нет, письмо не отправляется — об окончании договора сообщает только колокольчик администраторов.',
    }
  }
  // Письма о вопросах клиентов (client-questions) адресат вопроса получает лично — эти адреса
  // дополнительные.
  if (kind === 'question_created' || kind === 'question_client_replied') {
    return {
      intro:
        'Дополнительные адреса для письма о вопросе клиента. Адресат вопроса (ответственный, менеджер по сопровождению, менеджер клиента или администраторы) получает письмо лично.',
      empty: 'Пока дополнительных адресов нет, письмо получает только адресат вопроса.',
    }
  }
  return {
    intro: 'Адреса администратора и менеджеров, на которые приходит это письмо команде.',
    empty: 'Пока адресов нет, письмо не отправляется — о событии сообщает только колокольчик.',
  }
}

function makeRecipientRow(value: string): RecipientRow {
  recipientRowSeq += 1
  return { uid: recipientRowSeq, value }
}

/** Ошибки адресов из ответа 422: `recipients.N` → текст ошибки строки N (по отправленному списку). */
function recipientErrors(err: unknown): Map<number, string> {
  const out = new Map<number, string>()
  if (err instanceof ApiError && err.details) {
    for (const d of err.details) {
      const m = /^recipients\.(\d+)$/.exec(d.field)
      if (m) out.set(Number(m[1]), d.message)
    }
  }
  return out
}

function RecipientsEditor({
  template,
  onUpdated,
}: {
  template: AdminTemplate
  onUpdated: (template: AdminTemplate) => void
}) {
  const [rows, setRows] = useState<RecipientRow[]>(() =>
    template.recipients.map((e) => makeRecipientRow(e)),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map())
  const [ok, setOk] = useState(false)

  function reset() {
    setOk(false)
    setError(null)
    setRowErrors(new Map())
  }

  function change(uid: number, value: string) {
    reset()
    setRows((prev) => prev.map((row) => (row.uid === uid ? { ...row, value } : row)))
  }

  function remove(uid: number) {
    reset()
    setRows((prev) => prev.filter((row) => row.uid !== uid))
  }

  function add() {
    reset()
    setRows((prev) => [...prev, makeRecipientRow('')])
  }

  async function save() {
    setSaving(true)
    reset()
    try {
      const updated = await adminApi.notifications.patchTemplate(template.kind, {
        recipients: rows.map((row) => row.value),
      })
      onUpdated(updated)
      setRows(updated.recipients.map((e) => makeRecipientRow(e)))
      setOk(true)
    } catch (err) {
      // Номер ошибки — позиция в ОТПРАВЛЕННОМ списке, он совпадает с порядком строк.
      const byIndex = recipientErrors(err)
      setRowErrors(new Map([...byIndex].map(([i, msg]) => [rows[i]?.uid ?? -1, msg])))
      setError(getErrorMessage(err, 'Не удалось сохранить получателей'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-5 border-t border-line pt-4">
      <div className="mb-1 text-13 font-medium text-txt">Получатели письма</div>
      <Hint className="mb-3">
        {recipientsHint(template.kind).intro}
        {template.teamInbox
          ? ` Кроме них, письмо уходит на общий ящик команды ${template.teamInbox}.`
          : template.recipients.length === 0
            ? ` ${recipientsHint(template.kind).empty}`
            : null}
      </Hint>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {ok ? (
        <Alert variant="info" className="mb-3">
          Получатели сохранены.
        </Alert>
      ) : null}
      <div className="flex flex-col gap-2">
        {rows.map((row, i) => {
          const rowError = rowErrors.get(row.uid)
          return (
            <div key={row.uid}>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  aria-label={`Адрес получателя ${i + 1}`}
                  placeholder="name@centrika.ru"
                  value={row.value}
                  invalid={Boolean(rowError)}
                  onChange={(e) => change(row.uid, e.target.value)}
                  className="max-w-80"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={`Убрать адрес ${i + 1}`}
                  onClick={() => remove(row.uid)}
                  leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
                >
                  Убрать
                </Button>
              </div>
              {rowError ? (
                <p role="alert" className="mt-1 text-12 text-red">
                  {rowError}
                </p>
              ) : null}
            </div>
          )
        })}
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={add}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить адрес
        </Button>
        <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить получателей'}
        </Button>
      </div>
    </div>
  )
}

// --- Редактор шаблона: тема/текст + метки + гейт «предпросмотр + тест» ---

export function TemplateEditor({
  template,
  onUpdated,
}: {
  template: AdminTemplate
  onUpdated: (template: AdminTemplate) => void
}) {
  const [subject, setSubject] = useState(template.subject)
  const [bodyTemplate, setBodyTemplate] = useState(template.bodyTemplate)
  const [previewed, setPreviewed] = useState(false)
  const [tested, setTested] = useState(false)
  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [busy, setBusy] = useState<'preview' | 'test' | 'save' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [invalidPlaceholders, setInvalidPlaceholders] = useState<string[]>([])
  const [saved, setSaved] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  /** Любая правка темы/текста сбрасывает гейт безопасности (§3h). */
  function markDirty() {
    setPreviewed(false)
    setTested(false)
    setPreview(null)
    setSaved(false)
    setError(null)
    setInvalidPlaceholders([])
  }

  function changeSubject(value: string) {
    markDirty()
    setSubject(value)
  }

  function changeBody(value: string) {
    markDirty()
    setBodyTemplate(value)
  }

  /** Вставка метки `{{name}}` в текст по позиции курсора (или в конец). */
  function insertPlaceholder(name: string) {
    const token = `{{${name}}}`
    const el = bodyRef.current
    const start = el?.selectionStart ?? bodyTemplate.length
    const end = el?.selectionEnd ?? bodyTemplate.length
    const next = bodyTemplate.slice(0, start) + token + bodyTemplate.slice(end)
    changeBody(next)
    // Возвращаем фокус и ставим курсор после вставленной метки.
    requestAnimationFrame(() => {
      if (!bodyRef.current) return
      const pos = start + token.length
      bodyRef.current.focus()
      bodyRef.current.setSelectionRange(pos, pos)
    })
  }

  async function doPreview() {
    setBusy('preview')
    setError(null)
    try {
      const res = await adminApi.notifications.previewTemplate(template.kind, {
        subject,
        bodyTemplate,
      })
      setPreview(res)
      setPreviewed(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось показать предпросмотр'))
    } finally {
      setBusy(null)
    }
  }

  async function doTest() {
    setBusy('test')
    setError(null)
    try {
      await adminApi.notifications.testTemplate(template.kind, { subject, bodyTemplate })
      setTested(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отправить тестовое письмо'))
    } finally {
      setBusy(null)
    }
  }

  async function doSave() {
    setBusy('save')
    setError(null)
    setInvalidPlaceholders([])
    try {
      const updated = await adminApi.notifications.patchTemplate(template.kind, {
        subject,
        bodyTemplate,
      })
      onUpdated(updated)
      setSaved(true)
    } catch (err) {
      const invalid = invalidPlaceholderMessages(err)
      if (invalid.length > 0) {
        setInvalidPlaceholders(invalid)
      }
      setError(getErrorMessage(err, 'Не удалось сохранить шаблон'))
    } finally {
      setBusy(null)
    }
  }

  // Гейт «предпросмотр + тест» (§3h) + ни одна async-операция секции не выполняется.
  const canSave = previewed && tested && busy === null

  return (
    <Card header={`Редактор шаблона: ${template.label}`}>
      <Field label="Тема письма" required>
        {(props) => (
          <Input {...props} value={subject} onChange={(e) => changeSubject(e.target.value)} />
        )}
      </Field>

      <div className="my-4">
        <label htmlFor={`body-${template.kind}`} className="mb-1.5 block text-13 text-txt-secondary">
          Текст письма
          <RequiredMark />
        </label>
        <textarea
          id={`body-${template.kind}`}
          // Подпись помечена «*», а поле рисуется мимо `Field` — обязательность объявляем сами.
          aria-required
          ref={bodyRef}
          rows={8}
          className={TEXTAREA_CLASS}
          value={bodyTemplate}
          onChange={(e) => changeBody(e.target.value)}
        />
        <Hint className="mt-1.5">{EMAIL_LAYOUT_HINT}</Hint>
        {template.placeholders.length > 0 ? (
          <div className="mt-2">
            <Hint className="mb-1.5">Доступные метки (клик — вставить в текст):</Hint>
            <div className="flex flex-wrap gap-1.5">
              {template.placeholders.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => insertPlaceholder(p)}
                  className="inline-flex items-center rounded-7 border border-line-strong bg-white px-2 py-0.5 text-12 text-txt transition-colors hover:bg-hover"
                >
                  {`{{${p}}}`}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
          {invalidPlaceholders.length > 0 ? (
            <ul className="mt-1.5 list-disc pl-5">
              {invalidPlaceholders.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}

      {tested ? (
        <Alert variant="info" className="mb-3">
          Тестовое письмо отправлено вам.
        </Alert>
      ) : null}

      {saved ? (
        <Alert variant="info" className="mb-3">
          Шаблон сохранён.
        </Alert>
      ) : null}

      {preview ? (
        <div className="mb-3 rounded-8 border border-line bg-subtle px-4 py-3">
          <div className="text-12 text-txt-tertiary">Предпросмотр темы</div>
          <div className="mb-2 font-medium text-txt">{preview.subject}</div>
          <div className="text-12 text-txt-tertiary">Предпросмотр текста</div>
          <div className="whitespace-pre-wrap text-14 text-txt">{preview.body}</div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={() => void doPreview()} disabled={busy !== null}>
          {busy === 'preview' ? 'Загрузка…' : 'Предпросмотр'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => void doTest()} disabled={busy !== null}>
          {busy === 'test' ? 'Отправка…' : 'Тест себе'}
        </Button>
        <Button type="button" onClick={() => void doSave()} disabled={!canSave}>
          {busy === 'save' ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </div>
      <Hint className="mt-2">
        Сохранение доступно после предпросмотра и тест-отправки себе. Рабочее письмо уходит сразу всем
        адресатам, поэтому ошибку в шаблоне нужно поймать до отправки.
      </Hint>

      {template.editableOffsets ? <OffsetsEditor template={template} onUpdated={onUpdated} /> : null}
      {template.editableRecipients ? (
        <RecipientsEditor template={template} onUpdated={onUpdated} />
      ) : null}
    </Card>
  )
}

// --- Ручная разовая рассылка ---

export function BroadcastForm() {
  const [clients, setClients] = useState<ClientSummary[]>([])
  const [clientsTotal, setClientsTotal] = useState(0)
  const [clientsLoading, setClientsLoading] = useState(true)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [clientIds, setClientIds] = useState<Set<string>>(new Set())
  const [contacts, setContacts] = useState<Map<string, string>>(new Map())
  const [contactClient, setContactClient] = useState<string>('')
  const [clientContacts, setClientContacts] = useState<Contact[]>([])
  const [contactsError, setContactsError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sentCount, setSentCount] = useState<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setClientsLoading(true)
    teamApi
      .listClients({ limit: 200 }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) {
          setClients(res.items)
          setClientsTotal(res.total)
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить клиентов'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setClientsLoading(false)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!contactClient) {
      setClientContacts([])
      setContactsError(null)
      return
    }
    const controller = new AbortController()
    setClientContacts([])
    setContactsError(null)
    teamClientApi(contactClient)
      .listContacts(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setClientContacts(res.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setContactsError(getErrorMessage(err, 'Не удалось загрузить контакты'))
      })
    return () => controller.abort()
  }, [contactClient])

  function toggleClient(id: string, on: boolean) {
    setSentCount(null)
    setClientIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }

  function toggleContact(c: Contact, on: boolean, clientName: string) {
    setSentCount(null)
    setContacts((prev) => {
      const next = new Map(prev)
      if (on) next.set(c.id, `${c.fullName} (${clientName})`)
      else next.delete(c.id)
      return next
    })
  }

  const recipientCount = clientIds.size + contacts.size
  const canSend =
    subject.trim() !== '' && body.trim() !== '' && recipientCount > 0 && !sending

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSentCount(null)
    setSending(true)
    try {
      const res = await adminApi.notifications.broadcast({
        subject: subject.trim(),
        body: body.trim(),
        clientIds: clientIds.size > 0 ? [...clientIds] : undefined,
        contactIds: contacts.size > 0 ? [...contacts.keys()] : undefined,
      })
      setSentCount(res.sent)
      setSubject('')
      setBody('')
      setClientIds(new Set())
      setContacts(new Map())
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отправить рассылку'))
    } finally {
      setSending(false)
    }
  }

  const contactClientName = clients.find((c) => c.id === contactClient)?.name ?? ''

  return (
    <Card header="Ручная рассылка">
      <form onSubmit={onSubmit} noValidate>
        <Hint className="mb-3">
          Разовое письмо выбранным клиентам и/или контактам. Не привязано к событию, без повторов;
          фиксируется в журнале действий.
        </Hint>

        {error ? (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}
        {sentCount !== null ? (
          <Alert variant="info" className="mb-3">
            {`Отправлено: ${sentCount}`}
          </Alert>
        ) : null}

        <Field label="Тема письма" required>
          {(props) => (
            <Input
              {...props}
              value={subject}
              onChange={(e) => {
                setSentCount(null)
                setSubject(e.target.value)
              }}
            />
          )}
        </Field>

        <div className="my-4">
          <label htmlFor="broadcast-body" className="mb-1.5 block text-13 text-txt-secondary">
            Текст письма
            <RequiredMark />
          </label>
          <textarea
            id="broadcast-body"
            // Подпись помечена «*», а поле рисуется мимо `Field` — обязательность объявляем сами.
            aria-required
            rows={6}
            className={TEXTAREA_CLASS}
            value={body}
            onChange={(e) => {
              setSentCount(null)
              setBody(e.target.value)
            }}
          />
          <Hint className="mt-1.5">{EMAIL_LAYOUT_HINT}</Hint>
        </div>

        <div className="mt-4 border-t border-line pt-4">
          <div className="mb-2 text-13 font-medium text-txt">Клиенты</div>
          {clientsLoading ? (
            <Hint>Загрузка…</Hint>
          ) : clients.length === 0 ? (
            <Hint>Список клиентов пуст.</Hint>
          ) : (
            <>
              {clientsTotal > clients.length ? (
                <Hint className="mb-2">{`Показано ${clients.length} из ${clientsTotal} клиентов. Остальные доступны через раздел «Клиенты».`}</Hint>
              ) : null}
              <div className="flex max-h-60 flex-col gap-2 overflow-auto">
                {clients.map((c) => {
                  const checked = clientIds.has(c.id)
                  return (
                    <label key={c.id} className="flex items-center gap-2 text-14 text-txt">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => toggleClient(c.id, v === true)}
                      />
                      {c.name}
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <div className="mt-4 border-t border-line pt-4">
          <div className="mb-2 text-13 font-medium text-txt">Контакты (необязательно)</div>
          <Select value={contactClient} onValueChange={setContactClient}>
            <SelectTrigger aria-label="Загрузить контакты клиента" className="w-auto">
              <SelectValue placeholder="Загрузить контакты клиента" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {contactsError ? (
            <Alert variant="danger" className="mt-2">
              {contactsError}
            </Alert>
          ) : null}
          {contactClient && !contactsError ? (
            clientContacts.length === 0 ? (
              <Hint className="mt-2">У этого клиента нет контактов.</Hint>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                {clientContacts.map((ct) => (
                  <label key={ct.id} className="flex items-center gap-2 text-14 text-txt">
                    <Checkbox
                      checked={contacts.has(ct.id)}
                      onCheckedChange={(v) => toggleContact(ct, v === true, contactClientName)}
                    />
                    <span>
                      {ct.fullName}
                      {ct.email ? <span className="text-txt-tertiary">{` · ${ct.email}`}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            )
          ) : null}
          {contacts.size > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[...contacts.entries()].map(([id, label]) => (
                <Badge key={id} variant="blue">
                  {label}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Button
            type="submit"
            disabled={!canSend}
            leftIcon={<Send className="size-4 stroke-current" aria-hidden />}
          >
            {sending ? 'Отправка…' : 'Отправить'}
          </Button>
          <Hint>{`Адресатов выбрано: ${recipientCount}`}</Hint>
        </div>
      </form>
    </Card>
  )
}

// --- Страница ---

type Section = 'templates' | 'broadcast'

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'templates', label: 'Виды писем' },
  { id: 'broadcast', label: 'Ручная рассылка' },
]

export function AdminNotificationsPage() {
  const [templates, setTemplates] = useState<AdminTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [section, setSection] = useState<Section>('templates')
  const [selectedKind, setSelectedKind] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    adminApi.notifications
      .listTemplates(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setTemplates(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить виды писем'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  function onUpdated(updated: AdminTemplate) {
    setTemplates((prev) => prev.map((t) => (t.kind === updated.kind ? updated : t)))
  }

  const selected = useMemo(
    () => templates.find((t) => t.kind === selectedKind) ?? null,
    [templates, selectedKind],
  )

  return (
    <div>
      <PageHead
        title="Уведомления"
        subtitle="Тексты и расписание писем-уведомлений, ручные рассылки"
      />

      <EmailStatsPanel />

      <div className="mb-4 flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <Button
            key={s.id}
            size="sm"
            variant={section === s.id ? 'primary' : 'secondary'}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {section === 'templates' ? (
        error ? (
          <Alert variant="danger">{error}</Alert>
        ) : loading ? (
          <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : templates.length === 0 ? (
          <Panel>
            <EmptyState icon={Bell} title="Видов писем пока нет" description="Каталог уведомлений пуст" />
          </Panel>
        ) : (
          <>
            <TemplatesList
              templates={templates}
              selectedKind={selectedKind}
              onSelect={setSelectedKind}
              onUpdated={onUpdated}
            />
            {selected ? (
              <TemplateEditor key={selected.kind} template={selected} onUpdated={onUpdated} />
            ) : (
              <Panel>
                <EmptyState
                  icon={Mail}
                  title="Выберите вид письма"
                  description="Нажмите на вид в списке выше, чтобы отредактировать тему и текст"
                />
              </Panel>
            )}
          </>
        )
      ) : (
        <BroadcastForm />
      )}
    </div>
  )
}
